import createDOMPurify, { type DOMPurify } from 'dompurify';

/**
 * Sanitizes publisher-supplied HTML before it reaches `dangerouslySetInnerHTML`.
 *
 * Every call site that lands here — episode/feed descriptions, R2-cached full show notes,
 * and publisher-hosted transcript HTML — comes from an arbitrary third-party RSS feed. None
 * of it is generated or reviewed by this app, so none of it is trusted. The page's CSP
 * blocks inline `<script>` and non-image remote subresources, but that is a second layer of
 * defense, not a substitute for sanitizing: a `<style>` element is not a script and is not
 * touched by `script-src`, so unsanitized publisher HTML can inject arbitrary same-origin CSS
 * (data exfiltration via attribute selectors, phishing overlays) today, and an `onerror`/
 * `onload`/etc. handler is only one CSP misconfiguration or bypass away from executing.
 *
 * The allowlist keeps the formatting show notes actually use — paragraphs, line breaks,
 * emphasis, links, lists, headings, and images (the `.prose img` styling this app ships
 * exists because feeds routinely embed sponsor/episode art in their descriptions) — while
 * dropping anything that can run script, inject CSS, or otherwise reach outside the text
 * itself. `<style>`/`<script>` and all `on*` attributes are excluded by construction: they
 * are not in `ALLOWED_TAGS`/`ALLOWED_ATTR`, so DOMPurify strips them regardless of its own
 * defaults. `javascript:` (and other non-http(s)/mailto/tel) URLs in `href`/`src` are
 * rejected by DOMPurify's default `ALLOWED_URI_REGEXP`.
 */
const ALLOWED_TAGS = [
  'a',
  'p',
  'br',
  'ul',
  'ol',
  'li',
  'strong',
  'b',
  'em',
  'i',
  'u',
  's',
  'blockquote',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'span',
  'img',
  'code',
  'pre',
  'hr',
];

const ALLOWED_ATTR = ['href', 'src', 'alt', 'title'];

/** Every in-app link to an external site uses this pairing; see router.ts's link interceptor. */
function forceSafeExternalLinks(purify: DOMPurify): void {
  purify.addHook('afterSanitizeAttributes', (node) => {
    if (node.tagName === 'A' && node.hasAttribute('href')) {
      node.setAttribute('target', '_blank');
      node.setAttribute('rel', 'external noopener noreferrer');
    }
  });
}

// A DOMPurify instance is bound to one `window` for its lifetime (it caches document/DOM
// primitives off it at creation). A real page has exactly one `window` for the tab's whole
// life, so building this once and reusing it is correct and cheap there. It is memoized by
// `window` identity rather than built once at module load so that in a test runner that
// tears down and replaces the global DOM between files (this repo's `@vitest-environment
// happy-dom` files each get their own), a stale instance still pointing at a previous,
// already-torn-down `window` is never reused against the current one.
let purify: DOMPurify | undefined;
let purifyWindow: typeof window | undefined;

function getPurify(): DOMPurify {
  if (!purify || purifyWindow !== window) {
    purifyWindow = window;
    purify = createDOMPurify(window);
    forceSafeExternalLinks(purify);
  }
  return purify;
}

/**
 * Sanitizes untrusted publisher HTML (show notes, feed descriptions, transcript HTML) down
 * to a safe subset before it is handed to `dangerouslySetInnerHTML`.
 */
export function sanitizeShowNotesHtml(html: string): string {
  return getPurify().sanitize(html, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    ALLOW_DATA_ATTR: false,
    FORBID_TAGS: ['style', 'script'],
    FORBID_ATTR: ['style', 'class', 'id', 'srcset'],
  });
}
